import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

const HEX_PATTERN = /^#([A-Fa-f0-9]{6})$/;

export function hexColorValidator(required = false): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = (control.value ?? '').toString().trim();

        if (!value) {
            return required ? { required: true } : null;
        }

        return HEX_PATTERN.test(value) ? null : { hexColor: true };
    };
}

export function optionalUrlValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = (control.value ?? '').toString().trim();

        if (!value) {
            return null;
        }

        try {
            // Accept only absolute URLs for admin-entered home page links
            const url = new URL(value);
            return ['http:', 'https:'].includes(url.protocol) ? null : { url: true };
        } catch {
            return { url: true };
        }
    };
}

function hexToRgb(hex: string): [number, number, number] | null {
    const normalized = hex.replace('#', '');
    if (normalized.length !== 6) return null;

    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);

    return [r, g, b];
}

function luminance(hex: string): number {
    const rgb = hexToRgb(hex);
    if (!rgb) return 0;

    const [r, g, b] = rgb.map((v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(hex1: string, hex2: string): number | null {
    if (!HEX_PATTERN.test(hex1) || !HEX_PATTERN.test(hex2)) {
        return null;
    }

    const l1 = luminance(hex1);
    const l2 = luminance(hex2);

    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);

    return (lighter + 0.05) / (darker + 0.05);
}
